/**
Project: Voice Panel (c)
Title: Number Types
Description: Component for displaying the list of number types with search, sort and pagination
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Flex,
  Text,
  Input,
  Table,
  TableRow,
  SearchIcon,
  CloseIcon,
  Menu,
  tabListBehavior,
  Dialog
} from "@fluentui/react-northstar";
import axios from "axios";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import PageLoader from "../../common/helpers/page-loader";
import { Grid, Row, Col } from "react-flexbox-grid";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import TablePagination from "../../common/helpers/table-pagination";
import { NumberTypeTableHeaderColumns } from "../settings/number-type-table-header-columns";
import { ControlTab } from "../settings/control-tab";
import TableHeaderColumn from "../../common/helpers/table-header-column";
import { NumberTypesRow, AddNumberTypesRow } from "./number-type-row";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import AlertSuccessMessage from "../../common/helpers/alert-success-message";
import { AppConfigProps } from "../../../../core/settings/app-config";
import ResponseUtil from "../../../../core/helpers/response-util";
import { manageError } from "../../../../core/actions/common-actions";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import {
  setNumberTypesAllRecords,
  setNumberTypesDisplayRecords,
  setNumberTypesPageRecords,
  sliceNumberTypes,
  searchNumberTypes,
  sortNumberTypes,
  getNumberTypeList,
  addNumberType,
  updateNumberType,
  deleteNumberType,
} from "../actions/number-types-actions";
import { AppPageTitle } from "../../../../core/components/app-page-title";

class AdminNumberTypes extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_numbertypes = "vpa_numbertypes";

  constructor(props) {
    super(props);
    this.state = {
      pageOffset: VoiceAdminConstants.TABLE_DATA.PAGE_OFFSET_DEFAULT,
      pageLimit: VoiceAdminConstants.TABLE_DATA.PAGE_LIMIT_DEFAULT,
      sortedColumn: "numberTypeName",
      sortedOrder: VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING,
      isPageDataFetched: false,
      isNumberTypeDeleteModal: false,
      errorMessage: null,
      searchValue: "",
      searchKeyword: "",
      selectedNumber: "",
      addNumberType: false,
      showForm: false,
      successMessage: null,
      errorDetails: null,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.loadPageData();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  loadPageData = async () => {
    await this.setStateAsync({ isPageDataFetched: false });
    await this.clearAlert();
    await this.getNumberTypeList();
    await this.setStateAsync({ isPageDataFetched: true });
  };

  getNumberTypeList = async () => {
    await getNumberTypeList(this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data &&
          res.data.records
        ) {
          this.props.setNumberTypesAllRecords(res.data.records);
          let searchedRecords = await searchNumberTypes(
            this.props.numbertypesAllRecords,
            this.state.searchKeyword
          );
          let sortedRecords = await sortNumberTypes(
            searchedRecords,
            this.state.sortedColumn,
            this.state.sortedOrder
          );
          await this.props.setNumberTypesDisplayRecords(sortedRecords);
          let slicedRecords = await sliceNumberTypes(
            this.props.numbertypesDisplayRecords,
            this.state.pageOffset,
            this.state.pageLimit
          );
          await this.props.setNumberTypesPageRecords(slicedRecords);
        }
      })
      .catch(async (err) => {
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
        }
        await manageError(err, this.props.history);
      });
  };

  handleSearchChange = async (e) => {
    e.preventDefault();
    await this.setStateAsync({ searchValue: e.target.value });
  };

  handleSearchCancel = async (e) => {
    e.preventDefault();
    await this.clearAlert();
    let sortedRecords = await sortNumberTypes(
      this.props.numbertypesAllRecords,
      this.state.sortedColumn,
      this.state.sortedOrder
    );
    await this.props.setNumberTypesDisplayRecords(sortedRecords);

    let pageOffset = 0;
    let slicedRecords = await sliceNumberTypes(
      this.props.numbertypesDisplayRecords,
      pageOffset,
      this.state.pageLimit
    );
    await this.setStateAsync({ searchKeyword: "", searchValue: "" });
    await this.props.setNumberTypesPageRecords(slicedRecords);
    await this.setStateAsync({ pageOffset: pageOffset });
    await this.handleCloseForm();
    await this.handleCloseAddNumberType();
  };

  handleSearchSubmit = async (e) => {
    e.preventDefault();
    await this.clearAlert();
    if (
      this.props.numbertypesAllRecords &&
      this.props.numbertypesAllRecords.length > 0
    ) {
      let searchKeyword = this.state.searchValue;
      await this.setStateAsync({ searchKeyword: searchKeyword });
      let searchedRecords = await searchNumberTypes(
        this.props.numbertypesAllRecords,
        this.state.searchKeyword
      );
      let sortedRecords = await sortNumberTypes(
        searchedRecords,
        this.state.sortedColumn,
        this.state.sortedOrder
      );
      await this.props.setNumberTypesDisplayRecords(sortedRecords);

      let pageOffset = 0;
      let slicedRecords = await sliceNumberTypes(
        this.props.numbertypesDisplayRecords,
        pageOffset,
        this.state.pageLimit
      );
      await this.props.setNumberTypesPageRecords(slicedRecords);
      await this.setStateAsync({ pageOffset: pageOffset });
      await this.handleCloseForm();
      await this.handleCloseAddNumberType();
    }
  };

  handlePageOffsetChange = async (pageOffset) => {
    await this.clearAlert();
    if (pageOffset >= 0) {
      let slicedRecords = await sliceNumberTypes(
        this.props.numbertypesDisplayRecords,
        pageOffset,
        this.state.pageLimit
      );
      await this.props.setNumberTypesPageRecords(slicedRecords);
      await this.setStateAsync({ searchValue: this.state.searchKeyword });
      await this.setStateAsync({ pageOffset: pageOffset });
      await this.handleCloseForm();
      await this.handleCloseAddNumberType();
    }
  };

  handlePageLimitChange = async (pageLimit) => {
    await this.clearAlert();
    if (pageLimit > 0) {
      let pageOffset = 0;
      let slicedRecords = await sliceNumberTypes(
        this.props.numbertypesDisplayRecords,
        pageOffset,
        pageLimit
      );
      await this.props.setNumberTypesPageRecords(slicedRecords);
      await this.setStateAsync({ searchValue: this.state.searchKeyword });
      await this.setStateAsync({ pageOffset: pageOffset });
      await this.setStateAsync({ pageLimit: pageLimit });
      await this.handleCloseForm();
      await this.handleCloseAddNumberType();
    }
  };

  handleSortChange = async (sortColumn, sortOrder) => {
    await this.clearAlert();
    let sortedRecords = await sortNumberTypes(
      this.props.numbertypesDisplayRecords,
      sortColumn,
      sortOrder
    );
    await this.props.setNumberTypesDisplayRecords(sortedRecords);

    await this.setStateAsync({
      sortedColumn: sortColumn,
      sortedOrder: sortOrder,
    });

    let pageOffset = 0;
    let slicedRecords = await sliceNumberTypes(
      this.props.numbertypesDisplayRecords,
      pageOffset,
      this.state.pageLimit
    );

    await this.props.setNumberTypesPageRecords(slicedRecords);
    await this.setStateAsync({ searchValue: this.state.searchKeyword });
    await this.setStateAsync({ pageOffset: pageOffset });
    await this.handleCloseForm();
    await this.handleCloseAddNumberType();
  };

  handleOpenAddNumberType = async () => {
    await this.clearAlert();
    await this.setStateAsync({ numberType: true });
    await this.handleOpenForm();
  };

  handleCloseAddNumberType = async () => {
    await this.clearAlert();
    await this.setStateAsync({ numberType: false });
    await this.handleCloseForm();
  };

  handleOpenForm = async () => {
    await this.setStateAsync({ showForm: true });
  };

  handleCloseForm = async () => {
    await this.setStateAsync({ showForm: false });
  };

  clearAlert = async () => {
    await this.setStateAsync({
      errorMessage: null,
      errorDetails: null,
      successMessage: null,
    });
  };

  handleSaveNumberType = async (numbertype) => {
    await this.clearAlert();
    if (numbertype && numbertype.numberTypeName.trim() !== "") {
      const reg = /^[a-zA-Z0-9_\- ]*$/;
      if (reg.test(numbertype.numberTypeName) === true) {
        let duplicates = [];
        this.props.numbertypesAllRecords.forEach((type) => {
          if (
            type.numberTypeName.trim().toLowerCase() ===
            numbertype.numberTypeName.trim().toLowerCase()
          ) {
            duplicates.push(type);
          }
        });
        if (duplicates && duplicates.length > 0) {
          this.setState({
            errorMessage: IntlUtil.getSubstituteText(
              this._intl_ns_vpa_numbertypes,
              "notification.error.numberTypeExist",
              [
                {
                  key: "<NUMBERTYPE>",
                  value: numbertype.numberTypeName,
                },
              ]
            ),
          });
        } else {
          await this.props.showPageWaitDialog(
            IntlUtil.getText(
              this._intl_ns_vpa_numbertypes,
              "notification.info.pleaseWaitUpdatingData"
            )
          );
          let payload = {
            numberTypeName: numbertype?.numberTypeName ?? null,
            numberTypeNotes: numbertype?.NumberTypeNote ?? null,
          };
          await addNumberType(payload, this._cancelToken)
            .then(async (res) => {
              if (
                res &&
                res.status === AppConfigProps.httpStatusCode.ok &&
                res.data &&
                res.data.result
              ) {
                await this.getNumberTypeList();
                await this.handleCloseAddNumberType();
                await this.setStateAsync({
                  successMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.success.addNumberType"
                  ),
                });
                await this.props.hidePageWaitDialog();
              } else {
                await this.setStateAsync({
                  errorMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.error.addNumberTypeFailed"
                  ),
                });
                await this.props.hidePageWaitDialog();
              }
            })
            .catch(async (err) => {
              await this.props.hidePageWaitDialog();
              const errorStatus = ResponseUtil.getStatus(err);
              if (
                errorStatus &&
                errorStatus === AppConfigProps.httpStatusCode.badRequest
              ) {
                const primeError = ResponseUtil.getPrimeError(err);
                if (
                  primeError &&
                  (primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .ADD_NUMBERTYPE_FAILED ||
                    primeError.code ===
                      VoiceAdminConstants.SERVER_ERROR_CODE
                        .NUMBERTYPE_NAME_ALREADY_EXISTS ||
                    primeError.code ===
                      VoiceAdminConstants.SERVER_ERROR_CODE
                        .NUMBERTYPE_NAME_REQUIRED)
                ) {
                  await this.setStateAsync({
                    errorDetails: primeError.message,
                  });
                }
                await this.setStateAsync({
                  errorMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.error.addNumberTypeFailed"
                  ),
                });
              } else {
                await manageError(err, this.props.history);
              }
            });
        }
      } else {
        await this.setStateAsync({
          errorMessage: IntlUtil.getText(
            this._intl_ns_vpa_numbertypes,
            "notification.error.numberTypeNameNotValid"
          ),
        });
      }
    } else {
      await this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_numbertypes,
          "notification.error.numberTypeRequired"
        ),
      });
    }
  };

  handleUpdateNumberType = async (numberTypeId, numbertype) => {
    await this.clearAlert();
    if (numbertype && numbertype.numberTypeName.trim() !== "") {
      const reg = /^[a-zA-Z0-9_\- ]*$/;
      if (reg.test(numbertype.numberTypeName) === true) {
        let duplicates = [];
        this.props.numbertypesAllRecords.forEach((type) => {
          if (
            type.numberTypeId !== numberTypeId &&
            type.numberTypeName.trim().toLowerCase() ===
            numbertype.numberTypeName.trim().toLowerCase()
          ) {
            duplicates.push(type);
          }
        });
        if (duplicates && duplicates.length > 0) {
          this.setState({
            errorMessage: IntlUtil.getSubstituteText(
              this._intl_ns_vpa_numbertypes,
              "notification.error.numberTypeExist",
              [
                {
                  key: "<NUMBERTYPE>",
                  value: numbertype.numberTypeName.trim(),
                },
              ]
            ),
          });
        } else {
          await this.props.showPageWaitDialog(
            IntlUtil.getText(
              this._intl_ns_vpa_numbertypes,
              "notification.info.pleaseWaitUpdatingData"
            )
          );
          await updateNumberType(numberTypeId, numbertype, this._cancelToken)
            .then(async (res) => {
              if (
                res &&
                res.status === AppConfigProps.httpStatusCode.ok &&
                res.data &&
                res.data.result
              ) {
                await this.getNumberTypeList();
                await this.handleCloseForm();
                await this.props.hidePageWaitDialog();
                await this.setStateAsync({
                  successMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.success.updateNumberType"
                  ),
                });
              } else {
                await this.props.hidePageWaitDialog();
                await this.setStateAsync({
                  errorMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.error.updateNumberTypeFailed"
                  ),
                });
              }
            })
            .catch(async (err) => {
              await this.props.hidePageWaitDialog();
              const errorStatus = ResponseUtil.getStatus(err);
              if (
                errorStatus &&
                errorStatus === AppConfigProps.httpStatusCode.badRequest
              ) {
                const primeError = ResponseUtil.getPrimeError(err);
                if (
                  primeError &&
                  (primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .UPDATE_NUMBERTYPE_FAILED ||
                    primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .NUMBERTYPE_NAME_ALREADY_EXISTS ||
                    primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .NUMBERTYPE_NAME_REQUIRED)
                ) {
                  await this.setStateAsync({
                    errorDetails: primeError.message,
                  });
                }
                await this.setStateAsync({
                  errorMessage: IntlUtil.getText(
                    this._intl_ns_vpa_numbertypes,
                    "notification.error.updateNumberTypeFailed"
                  ),
                });
              } else {
                await manageError(err, this.props.history);
              }
            });
        }
      } else {
        await this.setStateAsync({
          errorMessage: IntlUtil.getText(
            this._intl_ns_vpa_numbertypes,
            "notification.error.numberTypeNameNotValid"
          ),
        });
      }
    } else {
      await this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_numbertypes,
          "notification.error.numberTypeRequired"
        ),
      });
    }
  };

  renderPaginationInfo = () => {
    if (
      this.props.numbertypesDisplayRecords &&
      this.props.numbertypesDisplayRecords.length > this.state.pageLimit
    ) {
      let start = this.state.pageOffset * this.state.pageLimit;
      let end = start + this.state.pageLimit;
      if (end > this.props.numbertypesDisplayRecords.length)
        end = this.props.numbertypesDisplayRecords.length;
      return (
        <>
          &nbsp;
          {IntlUtil.getSubstituteText(
            this._intl_ns_vpa_numbertypes,
            "content.displayingStartToEnd",
            [
              {
                key: "<START>",
                value: start + 1,
              },
              {
                key: "<END>",
                value: end,
              },
            ]
          )}
        </>
      );
    } else {
      return null;
    }
  };

  deleteNumber = async (numberTypeId) => {
    await this.clearAlert();
    await this.setStateAsync({ isNumberTypeDeleteModal: false });
    if (numberTypeId) {
      await this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_numbertypes,
          "notification.info.pleaseWaiDeletingNumberData"
        )
      );
      await deleteNumberType(numberTypeId, this._cancelToken)
        .then(async (res) => {
          if (res && res.status === AppConfigProps.httpStatusCode.ok) {
            let records = this.props.numbertypesAllRecords.filter((item) => {
              return item.numberTypeId !== res.data.result.numberTypeId
            })
            await this.setNumberTypesRecords(records);
            await this.setStateAsync({
              successMessage: IntlUtil.getText(
                this._intl_ns_vpa_numbertypes,
                "notification.success.deleteNumberType"
              ),
              showAlert: true,
            });
            await this.props.hidePageWaitDialog();
          } else {
            await manageError(res, this.props.history);
            await this.props.hidePageWaitDialog();
          }
        })
        .catch(async (err) => {
          await this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (
              primeError &&
              (primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE.UNABLE_DELETE_NUMBERTYPE_FAILED ||
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE.DELETE_NUMBERTYPE_INUSED_FAILED)
            ) {
              await this.setStateAsync({
                errorDetails: primeError.message,
              });
            }
            await this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_numbertypes,
                "notification.error.deleteNumberTypeFailed"
              ),
              showAlert: true,
            });
          } else {
            await manageError(err, this.props.history);
          }
        });
    }
  };

  setNumberTypesRecords = async (records) => {
    await this.props.setNumberTypesAllRecords(records);
    let searchedRecords = await searchNumberTypes(
      this.props.numbertypesAllRecords,
      this.state.searchKeyword
    );
    let sortedRecords = await sortNumberTypes(
      searchedRecords,
      this.state.sortedColumn,
      this.state.sortedOrder
    );
    await this.props.setNumberTypesDisplayRecords(sortedRecords);
    let slicedRecords = await sliceNumberTypes(
      this.props.numbertypesDisplayRecords,
      this.state.pageOffset,
      this.state.pageLimit
    );
    await this.props.setNumberTypesPageRecords(slicedRecords);
  }

  handleShowDeleteDialog = async (selectedNumberType) => {
    if (selectedNumberType) {
      await this.clearAlert();
      await this.setStateAsync({ selectedNumber: selectedNumberType });
      await this.setStateAsync({ isNumberTypeDeleteModal: true });
    }
  };

  handleHideDeleteDialog = async () => {
    await this.setStateAsync({ isNumberTypeDeleteModal: false });
  };

  renderNumberDeleteDialog = () => {
    if (this.state.selectedNumber) {
      return (
        <Dialog
          backdrop={true}
          cancelButton={IntlUtil.getText(
            this._intl_ns_vpa_numbertypes,
            "content.no"
          )}
          confirmButton={IntlUtil.getText(
            this._intl_ns_vpa_numbertypes,
            "content.yes"
          )}
          onConfirm={() => {
            this.deleteNumber(this.state.selectedNumber.numberTypeId);
          }}
          onCancel={() => {
            this.handleHideDeleteDialog();
          }}
          open={this.state.isNumberTypeDeleteModal}
          content={
            <Grid fluid className="p-l-0 p-r-0">
              <Row className="m-t-10 m-b-10 m-l-0 p-l-0">
                <Col xs={12} md={12} lg={12} xl={12}>
                  <Text className="vpa-page-dialog-info-text">
                    {IntlUtil.getText(
                      this._intl_ns_vpa_numbertypes,
                      "notification.info.deletePermanentlyTextPrefixNumberType"
                    )}
                    <b className="m-l-5">
                      {this.state.selectedNumber.numberTypeName}
                    </b>
                    {IntlUtil.getText(
                      this._intl_ns_vpa_numbertypes,
                      "notification.info.deletePermanentlyTextSuffix"
                    )}
                  </Text>
                </Col>
                <Col xs={12} md={12} lg={12} xl={12}>
                  <Text
                    className="vpa-page-dialog-info-text"
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_numbertypes,
                      "notification.info.deletePermanentlyText"
                    )}
                  />
                </Col>
              </Row>
            </Grid>
          }
          className="vpa-page-dialog"
        />
      );
    }
  };

  render() {
    return (
      <>
        <AppPageTitle
          pageTitle={IntlUtil.getText(
            this._intl_ns_vpa_numbertypes,
            "title.controlNumberTypes"
          )}
        />
        <Flex column className="page-wrapper">
          <Flex column className="page-title-wrapper wrapper-h m-b-20">
            <Menu
              className="nav nav-tabs"
              items={ControlTab(this._intl_ns_vpa_numbertypes)}
              accessibility={tabListBehavior}
            />
          </Flex>
          <Flex column className="vpa-page-content">
            <PageLoader
              status={this.state.isPageDataFetched}
              size="medium"
              labelPosition="below"
              label={IntlUtil.getText(
                this._intl_ns_common,
                "content.loadingInprogress"
              )}
              type="full-center"
            >
              <Grid fluid className="page-grid">
                <Row>
                  <Col xs={12} sm={12} md={12} lg={3} xl={2} className="m-b-10">
                    <Input
                      inline
                      inverted
                      placeholder={IntlUtil.getText(
                        this._intl_ns_vpa_numbertypes,
                        "content.search"
                      )}
                      value={this.state.searchValue}
                      onChange={this.handleSearchChange}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          this.handleSearchSubmit(e);
                        }
                      }}
                      className="vpa-search-input text-fs-r"
                      icon={
                        this.state.searchValue.length === 0 ? (
                          <SearchIcon
                            className="cursor-pointer vpa-search-icon"
                            onClick={this.handleSearchSubmit}
                          />
                        ) : (
                          <CloseIcon
                            className="cursor-pointer vpa-search-icon"
                            onClick={this.handleSearchCancel}
                          />
                        )
                      }
                    />
                  </Col>
                  <Col xs={12} sm={12} md={12} lg={7} xl={8}>
                    {this.state.errorMessage ? (
                      <AlertErrorMessage
                        content={this.state.errorMessage}
                        moreDetails={{
                          label: IntlUtil.getText(
                            this._intl_ns_vpa_numbertypes,
                            "content.moreDetails"
                          ),
                          content: this.state.errorDetails,
                        }}
                      />
                    ) : null}
                    {this.state.successMessage ? (
                      <AlertSuccessMessage
                        content={this.state.successMessage}
                      />
                    ) : null}
                  </Col>
                  <Col xs={12} sm={12} md={12} lg={2} xl={2} className="m-b-10">
                    {this.props.numbertypesDisplayRecords &&
                      this.props.numbertypesDisplayRecords.length > 0 ? (
                      <Row start="xs">
                        <Col xl={11} xlOffset={1}>
                          <Flex className="vpa-pagination-wrapper">
                            <TablePagination
                              pageOffset={this.state.pageOffset}
                              pageLimit={this.state.pageLimit}
                              totalRecords={
                                this.props.numbertypesDisplayRecords.length
                              }
                              handlePageOffsetChange={
                                this.handlePageOffsetChange
                              }
                              handlePageLimitChange={this.handlePageLimitChange}
                            />
                          </Flex>
                        </Col>
                      </Row>
                    ) : null}
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={12} md={8} lg={8} className="m-b-5">
                    {!this.props.numbertypesDisplayRecords ||
                      this.props.numbertypesDisplayRecords.length === 0 ? (
                      <Flex>
                        <Text className="page-text text-fs-s text-fc-dark">
                          {IntlUtil.getText(
                            this._intl_ns_vpa_numbertypes,
                            "content.noNumberTypesFound"
                          )}
                        </Text>
                      </Flex>
                    ) : (
                      <Flex>
                        <Text className="page-text text-fs-s text-fc-dark">
                          {IntlUtil.getSubstituteText(
                            this._intl_ns_vpa_numbertypes,
                            "content.countNumberTypesFound",
                            [
                              {
                                key: "<COUNT>",
                                value:
                                  this.props.numbertypesDisplayRecords.length,
                              },
                            ]
                          )}
                        </Text>
                        <Text className="page-text text-fs-s text-fc-dark">
                          {this.renderPaginationInfo()}
                        </Text>
                      </Flex>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Flex column className="vpa-table-wrapper">
                      <Table aria-label="table">
                        <Flex column className="vpa-table-header-wrapper">
                          <TableRow header className="vpa-table-header-row">
                            {NumberTypeTableHeaderColumns(
                              this._intl_ns_vpa_numbertypes
                            ).map((column) => {
                              return (
                                <TableHeaderColumn
                                  {...column}
                                  key={column.columnId}
                                  sortedColumn={this.state.sortedColumn}
                                  sortedOrder={this.state.sortedOrder}
                                  sortFunction={this.handleSortChange}
                                  buttonFunction={this.handleOpenAddNumberType}
                                  isDisabled={this.state.showForm}
                                />
                              );
                            })}
                          </TableRow>
                        </Flex>
                        <Flex column className="vpa-table-data-wrapper">
                          {this.state.numberType === true ? (
                            <AddNumberTypesRow
                              handleCloseAddNumberType={this.handleCloseAddNumberType}
                              handleSaveNumberType={this.handleSaveNumberType}
                            />
                          ) : null}
                          {this.props.numbertypesPageRecords.map(
                            (type, index) => {
                              return (
                                <NumberTypesRow
                                  type={type}
                                  key={`NumberType-${index}`}
                                  handleUpdateNumberType={this.handleUpdateNumberType}
                                  handleDeleteNumberType={this.handleShowDeleteDialog}
                                  clearAlert={this.clearAlert}
                                  showForm={this.state.showForm}
                                  handleOpenForm={this.handleOpenForm}
                                  handleCloseForm={this.handleCloseForm}
                                />
                              );
                            }
                          )}
                        </Flex>
                      </Table>
                    </Flex>
                  </Col>
                </Row>
              </Grid>
            </PageLoader>
            {this.renderNumberDeleteDialog()}
          </Flex>
        </Flex>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  numbertypesAllRecords: state.adminNumberTypeStore.numbertypesAllRecords,
  numbertypesDisplayRecords:
    state.adminNumberTypeStore.numbertypesDisplayRecords,
  numbertypesPageRecords: state.adminNumberTypeStore.numbertypesPageRecords,
  subscriberAccount: state.subscriberStore.subscriberAccount,
});

const mapActionToProps = {
  setNumberTypesAllRecords,
  setNumberTypesDisplayRecords,
  setNumberTypesPageRecords,
  showPageWaitDialog,
  hidePageWaitDialog,
};

export default connect(mapStateToProps, mapActionToProps)(AdminNumberTypes);
